(() => {
  'use strict';
  if (customElements.get('pb28-cart-drawer')) return;
  class PostbusDrawer extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.dialog = this.querySelector('dialog');
      this.busy = false;
      this.contents = this.querySelector('[data-pb28-contents]');
      this.root = this.dataset.rootUrl.replace(/\/?$/, '/');
      window.PostbusDrawer = this;
      this.dialog.addEventListener('cancel', event => { event.preventDefault(); this.close(); });
      this.dialog.addEventListener('close', () => { document.documentElement.classList.remove('pb28-cart-open'); });
      this.dialog.addEventListener('click', event => {
        if (event.target.closest('[data-pb28-close]')) { this.close(); return; }
        if (event.target === this.dialog) {
          const rect = this.dialog.getBoundingClientRect();
          if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.close();
        }
        const control = event.target.closest('[data-pb28-step],[data-pb28-remove]');
        if (!control || this.busy) return;
        const row = control.closest('[data-pb28-item]');
        const quantity = control.hasAttribute('data-pb28-remove') ? 0 : Math.max(0, Number(row.dataset.quantity) + Number(control.dataset.pb28Step));
        void this.change(row.dataset.key, quantity, control.dataset.pb28Step || 'remove');
      });
      this.dialog.addEventListener('submit', event => {
        if (!event.target.matches('[data-pb28-checkout-form]') || this.checkoutApproved) return;
        event.preventDefault();
        if (!this.busy) void this.checkout(event.target, event.submitter);
      });
      document.addEventListener('click', event => {
        const link = event.target.closest('[data-pb28-open]');
        if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        this.open();
        if (!this.busy) void this.load();
      });
      for (const name of ['pb15:cart-added', 'pb14:cart-added', 'pb28:cart-added']) document.addEventListener(name, event => {
        this.open();
        const html = event.detail?.response?.sections?.[this.dataset.sectionId];
        if (html && this.render(html)) { this.lock(false); this.status(this.dataset.added, 'added'); }
        else void this.load(this.dataset.added);
      });
    }
    open() {
      clearTimeout(this.closeTimer);
      if (!this.dialog.open) this.dialog.showModal();
      document.documentElement.classList.add('pb28-cart-open');
      requestAnimationFrame(() => this.dialog.classList.add('is-open'));
    }
    close() {
      this.dialog.classList.remove('is-open');
      this.closeTimer = setTimeout(() => this.dialog.close(), matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 180);
    }
    lock(value) {
      this.busy = value;
      this.dialog.classList.toggle('is-busy', value);
      this.contents.setAttribute('aria-busy', String(value));
      this.contents.querySelectorAll('button').forEach(button => {
        if (value) { button.dataset.pb28Disabled = String(button.disabled); button.disabled = true; }
        else if ('pb28Disabled' in button.dataset) { button.disabled = button.dataset.pb28Disabled === 'true'; delete button.dataset.pb28Disabled; }
      });
    }
    status(message, type = '') {
      const status = this.querySelector('[data-pb28-status]');
      status.textContent = message || '';
      status.hidden = !message;
      status.className = 'pb28-cart__status' + (type ? ' is-' + type : '');
      status.setAttribute('role', type === 'error' ? 'alert' : 'status');
      this.querySelector('[data-pb28-fallback]').hidden = type !== 'error';
    }
    begin() {
      this.open();
      this.lock(true);
      this.status(this.dataset.adding, 'loading');
    }
    fail(message) {
      this.lock(false);
      this.status(message || this.dataset.error, 'error');
    }
    render(html, key, action) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const next = doc.querySelector('[data-pb28-contents]');
      const count = doc.querySelector('[data-pb28-count]');
      if (!next || !count || !next.querySelector('[data-pb28-cart-state]')) return false;
      this.contents.replaceWith(next);
      this.contents = next;
      this.querySelector('[data-pb28-count]').textContent = count.textContent;
      this.lock(false);
      const cart = JSON.parse(next.querySelector('[data-pb28-cart-state]').textContent);
      document.dispatchEvent(new CustomEvent('postbus:cart-updated', { detail:{ cart } }));
      if (key) {
        const row = [...next.querySelectorAll('[data-pb28-item]')].find(item => item.dataset.key === key);
        const focus = row?.querySelector(action === 'remove' ? '[data-pb28-remove]' : `[data-pb28-step="${action}"]`) || next.querySelector('[data-pb28-step]') || this.querySelector('[data-pb28-close]');
        focus?.focus({preventScroll:true});
      }
      return true;
    }
    async request(url, options = {}, format = 'json') {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 15000);
      try {
        const response = await fetch(url, {credentials:'same-origin', cache:'no-store', ...options, signal:controller.signal});
        const data = format === 'text' ? await response.text() : await response.json();
        if (!response.ok) throw new Error(typeof data.description === 'string' ? data.description : this.dataset.error);
        return data;
      } catch (error) {
        if (['AbortError', 'TimeoutError', 'TypeError', 'SyntaxError'].includes(error.name)) throw new Error(this.dataset.error);
        throw error;
      } finally { clearTimeout(timer); }
    }
    async refresh(key, action) {
      const url = new URL(this.dataset.cartUrl, document.baseURI);
      url.searchParams.set('section_id', this.dataset.sectionId);
      const preview = new URL(location.href).searchParams.get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id', preview);
      const html = await this.request(url, {}, 'text');
      if (!this.render(html, key, action)) throw new Error(this.dataset.error);
    }
    async load(message) {
      this.lock(true);
      this.status(this.dataset.loading, 'loading');
      try { await this.refresh(); this.status(message || ''); }
      catch (error) { this.fail(error.message); }
      finally { this.lock(false); }
    }
    async change(key, quantity, action) {
      if (this.busy || !Number.isSafeInteger(quantity) || quantity < 0) return;
      this.lock(true);
      this.status(this.dataset.loading, 'loading');
      try {
        const data = await this.request(this.root + 'cart/change.js', {method:'POST', headers:{'Content-Type':'application/json', Accept:'application/json'}, body:JSON.stringify({id:key, quantity, sections:[this.dataset.sectionId], sections_url:this.dataset.cartUrl})});
        if (!this.render(data.sections?.[this.dataset.sectionId] || '', key, action)) await this.refresh(key, action);
        this.status(this.dataset.updated);
      } catch (error) {
        try { await this.refresh(key, action); } catch (_) {}
        this.fail(error.message);
      } finally { this.lock(false); }
    }
    async checkout(form, button) {
      this.lock(true);
      let approved = false;
      try {
        const initial = window.PostbusCartValidation.read(this.contents);
        await window.PostbusCartValidation.ensure({cartUrl:this.dataset.cartUrl, expectedItems:initial.items});
        approved = true;
      } catch (error) {
        let message = window.PostbusCartValidation?.message(error, this) || this.dataset.pbxValidation;
        if (error.message === 'pbx_cart_changed') {
          try { await this.refresh(); message = this.dataset.pbxChangedUpdated || message; } catch (_) {}
        }
        this.fail(message);
      }
      finally { this.lock(false); }
      if (approved && form.isConnected) {
        this.checkoutApproved = true;
        try { form.requestSubmit(button); }
        finally { this.checkoutApproved = false; }
      }
    }
  }
  customElements.define('pb28-cart-drawer', PostbusDrawer);
})();
